import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Calendar, CheckCircle, Lock, ArrowRight, Plus } from 'lucide-react';
import {
  useGetAnneesAcademiquesQuery,
  useGetAnneeActiveQuery,
} from '../../services/anneeAcademiqueApi';
import { setAnneeAcademique } from '../../services/baseApi';

const SelectionAnnee: React.FC = () => {
  const navigate = useNavigate();
  // La caisse arrive directement sur la consultation des résultats (2026-10-02).
  const role = useSelector((state: any) => state.auth.user?.role);
  const accueil = role === 'agent_caisse' ? '/caisse/resultats' : '/dashboard';
  const [selectedAnnee, setSelectedAnnee] = useState<number | null>(null);

  const { data: annees, isLoading, error } = useGetAnneesAcademiquesQuery();
  const { data: anneeActive } = useGetAnneeActiveQuery();
  // Fix 2026-08-25 : plus de useActiverAnneeMutation ici — la sélection est
  // purement locale (localStorage → header X-Annee-Academique).
  const isActivating = false;

  // Sélectionner l'année active par défaut
  useEffect(() => {
    if (anneeActive && !selectedAnnee) {
      setSelectedAnnee(anneeActive.id);
    }
  }, [anneeActive, selectedAnnee]);

  const handleSelectAnnee = () => {
    if (!selectedAnnee) return;
    // Fix 2026-08-25 : SÉLECTION LOCALE UNIQUEMENT (ne PAS activer en base).
    // L'ancienne version appelait `activerAnnee(selectedAnnee)` qui écrivait
    // `est_active=True` sur cette année, désactivait toutes les autres, et
    // basculait tous les portails (formateurs, étudiants, parents) sur cette
    // année. Pour changer l'année officielle du système, passer par la page
    // « Gestion des années académiques » avec son bouton dédié.
    const anneeSelectionnee = annees?.find(a => a.id === selectedAnnee);
    if (anneeSelectionnee) {
      setAnneeAcademique(anneeSelectionnee.libelle, anneeSelectionnee.id);
    }
    navigate(accueil);
  };

  const handleContinuerAvecActive = () => {
    if (anneeActive) {
      setAnneeAcademique(anneeActive.libelle, anneeActive.id);
      navigate(accueil);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Chargement des années académiques...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Erreur de chargement</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Impossible de charger les années académiques.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 dark:bg-blue-800 text-white p-6 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold">Sélection de l'Année Académique</h1>
          <p className="text-blue-100 mt-2">
            Choisissez l'année académique sur laquelle vous souhaitez travailler
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Année active actuelle */}
          {anneeActive && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">Année active actuelle</p>
                    <p className="text-lg font-bold text-green-800 dark:text-green-200">{anneeActive.libelle}</p>
                  </div>
                </div>
                <button
                  onClick={handleContinuerAvecActive}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <span>Continuer</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Liste des années */}
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {annees && annees.length > 0 ? (
              annees.map((annee) => (
                <div
                  key={annee.id}
                  onClick={() => !annee.est_cloturee && setSelectedAnnee(annee.id)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                    annee.est_cloturee
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 cursor-not-allowed opacity-60'
                      : selectedAnnee === annee.id
                      ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedAnnee === annee.id
                            ? 'border-blue-500 bg-blue-500 dark:border-blue-400 dark:bg-blue-400'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {selectedAnnee === annee.id && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{annee.libelle}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          {annee.est_active && (
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium rounded-full">
                              Active
                            </span>
                          )}
                          {annee.est_cloturee && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-full flex items-center">
                              <Lock className="w-3 h-3 mr-1" />
                              Clôturée
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {annee.est_cloturee && (
                      <Lock className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Aucune année académique disponible</p>
                <button
                  onClick={() => navigate('/annee-academique')}
                  className="mt-4 flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  <span>Créer une année</span>
                </button>
              </div>
            )}
          </div>

          {/* Bouton de validation */}
          {annees && annees.length > 0 && (
            <div className="mt-6 flex justify-between items-center">
              <button
                onClick={() => navigate('/annee-academique')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
              >
                <Plus className="w-4 h-4" />
                <span>Gérer les années</span>
              </button>
              <button
                onClick={handleSelectAnnee}
                disabled={!selectedAnnee || isActivating}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {isActivating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Activation...</span>
                  </>
                ) : (
                  <>
                    <span>Sélectionner cette année</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            Vous pourrez changer d'année académique ultérieurement depuis les paramètres
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectionAnnee;
